import {
  ADD_COMMAND,
  UPDATE_COMMAND_LIST_STATUS,
  UPDATE_FIRST_SCAN_FILE_PERCENTAGE,
  UPDATE_FIRST_SCAN_FILE_AMOUNT,
  COMMAND_ERROR,
  SHOW_PROCESSING_QUEUE,
  REMOVE_COMMAND,
  RESET_FILE_UPLOAD_CONTEXT,
  EXPAND_PROCESSING_QUEUE,
  SET_ERROR_IN_COMMAND,
  REMOVE_UNFINISHED_COMMANDS,
  MARK_COMMAND_FINISHED,
  PATCH_COMMAND
} from 'Common/Components/FileUploadContext/actions';
import {
  GROUPING_POLLING_STATES,
  GROUPING_SUPERSEDED_MESSAGE
} from 'Common/Components/FileUploadContext/groupingPolling/utils';

const initialState = {
  commandsInProcess: [],
  firstScanFilesAmount: 0,
  firstScanPercentage: 0,
  error: false,
  showProcessingQueue: false
};

const isGroupingCommand = (command = {}) => Boolean(command?.isGrouping);

const withCommandDefaults = (command = {}, queuedAt) => {
  if (!isGroupingCommand(command)) {
    return {
      ...command,
      queuedAt
    };
  }

  return {
    ...command,
    queuedAt,
    groupingStartTimestamp: command.groupingStartTimestamp ?? queuedAt,
    groupingPollingState: command.groupingPollingState ?? GROUPING_POLLING_STATES.IDLE,
    groupingPollingPhase: command.groupingPollingPhase ?? 'idle',
    groupingCommandResolved: command.groupingCommandResolved ?? false,
    awaitingLastGroupingCommand: command.awaitingLastGroupingCommand ?? false
  };
};

const markGroupingAsSuperseded = (command = {}) => {
  if (!isGroupingCommand(command) || command.finished) return command;

  return {
    ...command,
    finished: true,
    error: true,
    timeOut: false,
    warning: GROUPING_SUPERSEDED_MESSAGE,
    warnings: null,
    errorMessage: GROUPING_SUPERSEDED_MESSAGE,
    groupingPollingState: GROUPING_POLLING_STATES.ERROR,
    groupingPollingPhase: 'error',
    groupingCommandResolved: true,
    awaitingLastGroupingCommand: false
  };
};

const matchesCommandTarget = (command = {}, target = {}) => {
  if (!target) return false;

  if (Number.isFinite(Number(target.queuedAt)) && Number(command?.queuedAt) === Number(target.queuedAt)) {
    return true;
  }

  if (target.mockCommandId && command?.mockCommandId === target.mockCommandId) {
    return true;
  }

  if (target.commandId && String(command?.command_id ?? '') === String(target.commandId)) {
    return true;
  }

  return false;
};

function fileUploaderContext (state = initialState, action) {
  switch (action.type) {
    case SHOW_PROCESSING_QUEUE: {
      return {
        ...state,
        showProcessingQueue: action.show,
        processingQueueisExpanded: action.show
      };
    }

    case EXPAND_PROCESSING_QUEUE: {
      return {
        ...state,
        processingQueueisExpanded: action.expand
      };
    }

    case ADD_COMMAND: {
      const queuedAt = action.command?.queuedAt ?? Date.now();
      const nextCommand = withCommandDefaults(action.command, queuedAt);
      const commandsInProcess = isGroupingCommand(nextCommand)
        ? state.commandsInProcess.map(markGroupingAsSuperseded)
        : state.commandsInProcess;

      return {
        ...state,
        commandsInProcess: [
          ...commandsInProcess,
          nextCommand
        ]
      };
    }

    case UPDATE_COMMAND_LIST_STATUS: {
      return {
        ...state,
        commandsInProcess: action.data
      };
    }

    case UPDATE_FIRST_SCAN_FILE_AMOUNT: {
      return {
        ...state,
        firstScanFilesAmount: action.value
      };
    }
    case UPDATE_FIRST_SCAN_FILE_PERCENTAGE: {
      return {
        ...state,
        firstScanFilesPercentage: action.value
      };
    }

    case COMMAND_ERROR: {
      return {
        ...state,
        error: true
      };
    }

    case REMOVE_COMMAND: {
      const commands = [...state.commandsInProcess];
      const filterList = action.mockCommandId ? commands.filter((command) => command.mockCommandId !== action.mockCommandId) : commands.filter((command) => command.command_id !== action.commandId);
      return {
        ...state,
        commandsInProcess: filterList
      };
    }

    case SET_ERROR_IN_COMMAND: {
      const commands = [...state.commandsInProcess];
      const filterList = action.mockCommandId ? commands.filter((command) => command.mockCommandId !== action.mockCommandId) : commands.filter((command) => command.command_id !== action.commandId);
      const command = {
        mockCommandId: action.mockCommandId,
        name: action.name,
        fileType: 'file',
        error: true,
        errorMessage: action.errorMessage,
        finished: true
      };

      return {
        ...state,
        commandsInProcess: [
          ...filterList,
          command
        ]
      };
    }

    case RESET_FILE_UPLOAD_CONTEXT: {
      return {
        ...state,
        commandsInProcess: initialState.commandsInProcess
      };
    }

    case REMOVE_UNFINISHED_COMMANDS: {
      const commands = [...state.commandsInProcess];
      const filteredCommands = commands.filter((command) => command.finished);

      return {
        ...state,
        commandsInProcess: filteredCommands
      };
    }

    case MARK_COMMAND_FINISHED: {
      return {
        ...state,
        commandsInProcess: state.commandsInProcess.map((c) =>
          c.mockCommandId === action.mockCommandId
            ? { ...c, finished: true, error: false, ...action.patch }
            : c
        )
      };
    }

    case PATCH_COMMAND: {
      return {
        ...state,
        commandsInProcess: state.commandsInProcess.map((command) =>
          matchesCommandTarget(command, action.target)
            ? { ...command, ...action.patch }
            : command
        )
      };
    }

    default: {
      return state;
    }
  }
}

export default fileUploaderContext;
